<template>
  <transition name="loader-transition" mode="out-in">
    <div class="base" v-if="user">
      <div class="base__sidebar">
        <NavBar :user="user" :project-id="projectId">
          <template #button>
            <transition name="fade-transition" mode="out-in">
              <AnimatedButton
                v-if="showButton"
                data-test-id="licensing-action-button"
                @click="showBannerIfLimited = true"
              />
            </transition>
          </template>
        </NavBar>
      </div>

      <div class="base__content">
        <router-view v-slot="{ Component }">
          <transition name="fade-transition" mode="out-in">
            <component :is="Component" :user="user" :key="$route.params.projectId" />
          </transition>
        </router-view>

        <transition name="fade-transition" mode="out-in">
          <LicensingBanner v-if="showBanner" @close="showBannerIfLimited = false" />
        </transition>
      </div>

      <Teleport to="body">
        <transition-group name="modal-transition">
          <template v-if="kickoutLeaseModal">
            <Modal :key="kickoutLeaseModal.type">
              <KickoutLeaseCard />
            </Modal>
          </template>
          <template v-else>
            <Modal
              v-for="modal in baseModals"
              :key="modal.type"
              :style="{ 'z-index': modal.zIndex }"
              :close-on-backdrop-click="modal.type !== 'userImageEdit'"
              @cancel="$store.dispatch('modal/hide', modal)"
              @vue:beforeUnmount="$store.dispatch('modal/hide', modal)"
              :data-test-id="modal.tId"
            >
              <CreateProjectCard
                v-if="modal.type === 'projectCreate'"
                :user="user"
                @create="createProject"
                @cancel="$store.dispatch('modal/hide', modal)"
              />
              <UserImageEditCard
                v-else-if="modal.type === 'userImageEdit'"
                :file="modal.payload"
                @cancel="$store.dispatch('modal/hide', modal)"
                @done="
                  (blob) => $store.dispatch('modal/hide', { type: 'userImageEdit', reason: 'done', payload: blob })
                "
              />
              <GlobalSearchModal
                v-else-if="modal.type === 'globalSearch'"
                @close="$store.dispatch('modal/hide', modal)"
                data-test-id="global-search-modal"
              />
              <component
                v-else
                :is="modal.type"
                v-bind="modal.payload"
                @cancel="$store.dispatch('modal/hide', modal)"
              />
            </Modal>
          </template>
        </transition-group>
      </Teleport>
    </div>
  </transition>
</template>
<script lang="ts">
import { defineComponent, defineAsyncComponent } from 'vue';
import { mapGetters, mapState } from 'vuex';

import NavBar from '@components/NavBar/NavBar';
import Onboarding from '@components/Onboarding';
import CreateProjectCard from '@components/CreateProjectCard';

import { loadLanguageAsync, getLocalLang } from '@/i18n';
import { commandMixin } from '@/mixins/commandMixin';
import { shortcutMixin, ShortcutConfig } from '@shortcuts/shortcutMixin';
import ShortcutCard from '@components/ShortcutCard';
import LicenseTrialStartupCard from '@components/license/LicenseTrialStartupCard';
import LimitedVersionCard from '@components/license/LimitedVersionCard';
import HelpscoutStatisticsCard from '@components/HelpscoutStatisticsCard';
import LicenseUpgradeCard from '@components/license/LicenseUpgradeCard';
import LicenseNoSeatCard from '@components/license/LicenseNoSeatCard';
import LicenseUnverifiedCard from '@components/license/LicenseUnverifiedCard';
import LoginExpiredCard from '@components/LoginExpiredCard';
import { user as userPollInterval } from '@utils/pollIntervals';
import * as helpscout from '@/helpscout';
import { User as HelpscoutUser } from '@/helpscout/types';

import AnimatedButton from '@components/AnimatedButton.vue';
import KickoutLeaseCard from '@components/license/KickoutLeaseCard.vue';
import { USER_LOGOUT } from '@/store/mutation-types';
import { UserDocument } from '@/graphql/operations.gen';
import { User } from '@/types';
import { Command } from '@/store/types';

const UserImageEditCard = defineAsyncComponent(
  () => import(/* webpackChunkName: "misc" */ '@components/UserImageEditCard.vue')
);

const GlobalSearchModal = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "projectModals", webpackPrefetch: true */ '@components/GlobalSearch/GlobalSearchModal.vue'
    )
);

const baseModals = [
  'HelpscoutStatisticsCard',
  'LicenseNoSeatCard',
  'LicenseTrialStartupCard',
  'LicenseUpgradeCard',
  'LicenseUnverifiedCard',
  'LimitedVersionCard',
  'LoginExpiredCard',
  'Onboarding',
  'projectCreate',
  'ShortcutCard',
  'userImageEdit',
  'globalSearch',
];

// kickoutLeaseCard should be displayed exclusively
// as soon kickoutLeaseCard is dismissed, modals listed in baseModals will be displayed again
const priorityModal = 'kickoutLeaseCard';

export default defineComponent({
  name: 'Root',

  components: {
    AnimatedButton,
    NavBar,
    Onboarding,
    CreateProjectCard,
    ShortcutCard,
    UserImageEditCard,
    HelpscoutStatisticsCard,
    LicenseNoSeatCard,
    LicenseTrialStartupCard,
    LimitedVersionCard,
    LicenseUpgradeCard,
    LicenseUnverifiedCard,
    LoginExpiredCard,
    KickoutLeaseCard,
    GlobalSearchModal,
  },

  mixins: [commandMixin, shortcutMixin],

  commands(): Array<Command> {
    const commands: Array<Command> = [
      {
        icon: 'AddIcon16',
        shortcut: 'create:project',
        translationKey: 'shortcutModal.createproject',
        dataTestId: 'global-search-create-project-button',
        callback: () => {
          this.$store.dispatch('modal/show', { type: 'projectCreate', label: 'shortcut' });
        },
        group: 'create',
      },
      {
        icon: 'AtlasTiIcon16',
        shortcut: 'go:projects',
        translationKey: 'shortcutModal.goprojects',
        dataTestId: 'global-search-go-to-projects-view-button',
        route: { name: 'projects' },
        group: 'navigation',
      },
    ];

    if (process.env.NODE_ENV === 'development') {
      commands.push({
        icon: 'SettingsIcon16',
        iconRight: 'SettingsIcon16',
        group: 'navigation',
        translationKey: 'action.goToProjectTemplateImport',
        dataTestId: 'global-search-project-template-import-button',
        route: { name: 'projectTemplateImport' },
      });
    }

    return commands;
  },

  props: {
    projectId: {
      type: String,
      default: null,
    },
  },

  data(): {
    user: User;
    unsubscribeLogout: () => void;
    showBannerIfLimited: boolean;
  } {
    return {
      user: null,
      unsubscribeLogout: () => {},
      showBannerIfLimited: true,
    };
  },

  apollo: {
    /**
     * Queries user base information from cache (set by authenticate).
     */
    user() {
      return {
        query: UserDocument,
        fetchPolicy: 'cache-only',
        nextFetchPolicy: 'cache-first',
        pollInterval: userPollInterval,
        result(result) {
          const user = result.data.user;

          if (user.locale && (!getLocalLang() || getLocalLang() !== user.locale)) {
            loadLanguageAsync(user.locale);
          }
        },
        error({ graphQLErrors }) {
          if (graphQLErrors) {
            graphQLErrors.forEach((error) => {
              console.error(error);
            });
          }
        },
      };
    },
  },

  computed: {
    ...mapState('modal', {
      baseModals: (state) => state.modals.filter((modal) => baseModals.includes(modal.type)),
      kickoutLeaseModal: (state) => state.modals.find((modal) => modal.type === priorityModal),
    }),
    ...mapState('user', ['canHaveTrial', 'lease']),
    ...mapGetters({
      isLimited: 'user/isLimited',
      analyticsConsent: 'tracking/analyticsConsent',
    }),

    showBanner(): boolean {
      return this.isLimited && this.showBannerIfLimited;
    },

    showButton(): boolean {
      return this.isLimited && !this.showBannerIfLimited;
    },

    helpscoutUser(): HelpscoutUser {
      if (!this.user) {
        return null;
      }

      return {
        name: this.user.fullname,
        email: this.user.email,
        signature: this.user.helpscoutHashes[this.user.locale] || this.user.helpscoutHashes.default,
        language: this.user.locale,
        'license-id': this.lease?.license.display_id,
      };
    },
  },

  shortcuts(): ShortcutConfig {
    return {
      'global-search:open': () => {
        this.$store.dispatch('modal/show', { type: 'globalSearch' });

        return false;
      },
      help: () => this.$store.dispatch('modal/show', { type: 'ShortcutCard', stackable: true, label: 'shortcut' }),
    };
  },

  methods: {
    createProject(data) {
      this.$store
        .dispatch('project/create', data)
        .then(({ id: projectId }) => {
          this.$store.dispatch('modal/hide', { type: 'projectCreate' });

          this.$router.push({
            name: 'sources',
            params: {
              projectId,
            },
          });
        })
        .catch((error) => {
          console.error('error', error);
        });
    },
  },

  created() {
    helpscout.init();

    // skip all subscriptions after logout, because otherwise vue-apollo will
    // retry endlessly
    this.unsubscribeLogout = this.$store.subscribe((mutation) => {
      if (mutation.type === `user/${USER_LOGOUT}`) {
        this.$apollo.skipAllSubscriptions = true;
      }
    });
  },
  beforeUnmount() {
    this.unsubscribeLogout();
  },

  watch: {
    analyticsConsent(val) {
      // update helpscout identification status if user changes analytics preferences
      if (val) {
        helpscout.identify(this.helpscoutUser);
      } else {
        helpscout.logout();
        this.$tracker.destroy();
      }
    },
    helpscoutUser: {
      deep: true,
      handler(user, oldUser) {
        // user can be null, e.g. after logout
        if (user) {
          helpscout.refresh(user, oldUser, this.analyticsConsent);
        }
      },
    },
  },
});
</script>
<style lang="scss">
.base {
  height: 100%;
  display: flex;
}

.base__sidebar {
  flex: 0 0 auto;
}

.base__content {
  flex: 1 1 auto;
  min-width: 0;
}
</style>
